// /credits-Page — Credit-Abos (dominant) + Einzel-Pack-Kauf (sekundär) via Stripe.
// Abo-Sektion: monthly/yearly-Toggle, aktives-Abo-Status-Karte (Portal-Verwaltung)
// oder Plan-Karten (Subscription-Checkout) mit Benefit-Content + Pro-Highlight.
// Darunter kompakte Einzel-Packs (GET /credits/packs).
// Wallet-Summary (Monats- vs. gekaufte Credits) via useCredits.
// Liest URL ?status=success|cancel|sub_success für Banner.

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckCircle2,
  Crown,
  Loader2,
  RefreshCw,
  Settings2,
  Sparkles,
  Wallet,
} from 'lucide-react';
import {
  createCreditCheckout,
  fetchCreditPacks,
  type CreditPack,
} from '@/lib/credits-api';
import {
  createSubscriptionCheckout,
  openSubscriptionPortal,
  type BillingCycle,
} from '@/lib/subscriptions-api';
import { useCredits } from '@/hooks/use-credits';
import { useSubscription } from '@/hooks/use-subscription';
import { selectAccessToken, useAuthStore } from '@/stores/auth-store';
import { useEvePageContextStore } from '@/stores/eve-page-context-store';
import { PlanCard } from '@/components/credits/plan-card';
import { PackCard } from '@/components/credits/pack-card';
import { getPlanContent } from '@/components/credits/plan-content';

/** ID des hervorgehobenen "Pro"-Plans. */
const RECOMMENDED_PLAN_ID = 'plan_m';

/** Formatiert ein ISO-Datum als de-DE-Tagesdatum, leer-tolerant. */
function formatResetDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function CreditsPage() {
  const accessToken = useAuthStore(selectAccessToken);
  const { balance, monthly, purchased, refresh } = useCredits();
  const { plans, active, loading: subLoading, refresh: refreshSub } = useSubscription();

  const [packs, setPacks] = useState<ReadonlyArray<CreditPack>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkoutPackId, setCheckoutPackId] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const [cycle, setCycle] = useState<BillingCycle>('monthly');
  const [subPlanInFlight, setSubPlanInFlight] = useState<string | null>(null);
  const [portalInFlight, setPortalInFlight] = useState(false);
  const [subError, setSubError] = useState<string | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const setPageContext = useEvePageContextStore((s) => s.setPageContext);
  const clearPageContext = useEvePageContextStore((s) => s.clearPageContext);

  const status = searchParams.get('status');

  // Baseline = teuerster Preis-pro-Credit (günstigster Tier) für dynamisches Rabatt-Pill.
  const baselinePricePerCredit = useMemo(() => {
    let max = 0;
    for (const p of plans) {
      if (p.credits_per_month <= 0) continue;
      const ppc = p.price_monthly_cents / p.credits_per_month;
      if (ppc > max) max = ppc;
    }
    return max;
  }, [plans]);

  /** Rabatt pro Credit vs. günstigstem Tier (immer aus Monatspreis, als Wert-Indikator). */
  function discountForPlan(creditsPerMonth: number, monthlyCents: number): number {
    if (baselinePricePerCredit <= 0 || creditsPerMonth <= 0) return 0;
    const ppc = monthlyCents / creditsPerMonth;
    const pct = Math.round((1 - ppc / baselinePricePerCredit) * 100);
    return pct > 0 ? pct : 0;
  }

  // Pack-Liste laden.
  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const res = await fetchCreditPacks(accessToken);
        if (!cancelled) {
          setPacks(res);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Pakete konnten nicht geladen werden.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  // Nach Stripe-Rückkehr: Erfolg → Balance/Abo refetchen, Status aus URL räumen.
  useEffect(() => {
    if (status === 'success' || status === 'sub_success') {
      refresh();
      refreshSub();
    }
    if (status === 'success' || status === 'sub_success' || status === 'cancel') {
      const timer = setTimeout(() => {
        setSearchParams(
          (p) => {
            p.delete('status');
            return p;
          },
          { replace: true },
        );
      }, 8000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [status, refresh, refreshSub, setSearchParams]);

  // EVE-Page-Context setzen.
  useEffect(() => {
    setPageContext({
      pageKey: '/credits',
      title: 'Credits aufladen',
      summary:
        balance !== null
          ? `Aktueller Stand: ${balance} Credits (${monthly} Monats + ${purchased} gekauft). ${active ? 'Abo aktiv.' : `${plans.length} Abo-Pläne, ${packs.length} Pakete verfügbar.`}`
          : `${plans.length} Abo-Pläne, ${packs.length} Pakete verfügbar.`,
    });
    return () => clearPageContext();
  }, [balance, monthly, purchased, active, plans.length, packs.length, setPageContext, clearPageContext]);

  async function handleBuy(packId: string) {
    if (!accessToken) return;
    setCheckoutPackId(packId);
    setCheckoutError(null);
    try {
      const res = await createCreditCheckout(accessToken, packId);
      window.location.href = res.url;
    } catch {
      setCheckoutError('Kauf konnte nicht gestartet werden. Versuch es nochmal.');
      setCheckoutPackId(null);
    }
  }

  async function handleSubscribe(planId: string) {
    if (!accessToken) return;
    setSubPlanInFlight(planId);
    setSubError(null);
    try {
      const res = await createSubscriptionCheckout(accessToken, planId, cycle);
      window.location.href = res.url;
    } catch {
      setSubError('Abo konnte nicht gestartet werden. Versuch es nochmal.');
      setSubPlanInFlight(null);
    }
  }

  async function handleManage() {
    if (!accessToken) return;
    setPortalInFlight(true);
    setSubError(null);
    try {
      const res = await openSubscriptionPortal(accessToken);
      window.location.href = res.url;
    } catch {
      setSubError('Verwaltung konnte nicht geöffnet werden. Versuch es nochmal.');
      setPortalInFlight(false);
    }
  }

  const subInFlight = subPlanInFlight !== null || portalInFlight;

  return (
    <div className="h-full overflow-y-auto p-5 md:p-8">
      <div className="mx-auto max-w-5xl space-y-10">
        {/* ── Header + Wallet-Summary ──────────────────────────────── */}
        <header className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Sparkles className="size-5 text-amber-400" />
              <h1 className="text-2xl font-bold text-slate-100">Credits &amp; Abos</h1>
            </div>
            <p className="mt-1 text-[13px] text-slate-500">
              Monatlich auffüllende Abos oder einmalige Pakete — du entscheidest.
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 240, damping: 24 }}
            className="glass-strong flex items-center gap-4 rounded-2xl border border-slate-700/40 px-5 py-4"
          >
            <div className="grid size-11 place-items-center rounded-xl border border-amber-500/30 bg-amber-500/10">
              <Wallet className="size-5 text-amber-400" />
            </div>
            <div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-3xl font-bold tabular-nums text-amber-300">
                  {balance !== null ? balance.toLocaleString('de-DE') : '—'}
                </span>
                <span className="text-[13px] font-medium text-slate-400">Credits</span>
              </div>
              {balance !== null ? (
                <p className="mt-0.5 text-[11px] text-slate-500">
                  {monthly.toLocaleString('de-DE')} Monats-Credits ·{' '}
                  {purchased.toLocaleString('de-DE')} gekauft ·{' '}
                  {balance.toLocaleString('de-DE')} gesamt
                </p>
              ) : (
                <p className="mt-0.5 text-[11px] text-slate-500">Aktueller Stand</p>
              )}
            </div>
          </motion.div>
        </header>

        {/* ── Status-Banner ────────────────────────────────────────── */}
        {status === 'success' ? (
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-[13px] text-emerald-300">
            Zahlung erhalten — deine Credits werden gutgeschrieben.
          </div>
        ) : null}
        {status === 'sub_success' ? (
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-[13px] text-emerald-300">
            Abo aktiv — deine Monats-Credits sind da.
          </div>
        ) : null}
        {status === 'cancel' ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-[13px] text-slate-400">
            Kauf abgebrochen.
          </div>
        ) : null}

        {/* ── Abo-Sektion (dominant) ───────────────────────────────── */}
        <section className="space-y-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2.5">
              <RefreshCw className="size-5 text-amber-400" />
              <div>
                <h2 className="text-lg font-bold text-slate-100">Abos</h2>
                <p className="text-[12px] text-slate-500">Credits, die sich monatlich auffüllen</p>
              </div>
            </div>

            {!active ? (
              <div className="flex items-center gap-2">
                {cycle === 'yearly' ? (
                  <span className="rounded-full border border-amber-500/40 bg-amber-500/15 px-2.5 py-1 text-[11px] font-bold text-amber-300">
                    2 Monate gratis
                  </span>
                ) : null}
                <div className="inline-flex items-center rounded-xl border border-slate-700/40 bg-white/[0.02] p-1 text-[12px]">
                  <button
                    type="button"
                    onClick={() => setCycle('monthly')}
                    className={`rounded-lg px-3.5 py-1.5 font-semibold transition-colors ${
                      cycle === 'monthly'
                        ? 'bg-amber-500/20 text-amber-300'
                        : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    Monatlich
                  </button>
                  <button
                    type="button"
                    onClick={() => setCycle('yearly')}
                    className={`rounded-lg px-3.5 py-1.5 font-semibold transition-colors ${
                      cycle === 'yearly'
                        ? 'bg-amber-500/20 text-amber-300'
                        : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    Jährlich
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          {subLoading ? (
            <div className="py-10 text-center text-[13px] text-slate-500">
              <Loader2 className="mx-auto mb-2 size-5 animate-spin" />
              Lade Abo-Pläne…
            </div>
          ) : active ? (
            <div className="glass-strong rounded-2xl border border-amber-500/40 p-6 ring-1 ring-amber-500/20">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="grid size-11 shrink-0 place-items-center rounded-xl border border-amber-500/30 bg-amber-500/10">
                    <Crown className="size-5 text-amber-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-bold text-slate-100">
                        {plans.find((p) => p.id === active.plan_id)?.name ?? active.plan_id}
                      </h3>
                      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[11px] font-semibold text-emerald-300">
                        <CheckCircle2 className="size-3" />
                        Aktiv
                      </span>
                    </div>
                    <p className="mt-1 text-[13px] text-slate-400">
                      {active.billing_cycle === 'yearly' ? 'Jährlich' : 'Monatlich'} · nächste
                      Auffüllung am{' '}
                      <span className="text-slate-200">{formatResetDate(active.monthly_reset_at)}</span>
                    </p>
                    {active.cancel_at_period_end ? (
                      <p className="mt-1 text-[12px] text-amber-300">
                        Kündigung zum Periodenende vorgemerkt.
                      </p>
                    ) : null}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => void handleManage()}
                  disabled={subInFlight}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-700/40 bg-white/[0.03] px-4 py-2.5 text-[13px] font-semibold text-slate-200 transition-colors hover:bg-white/[0.06] disabled:opacity-40"
                >
                  {portalInFlight ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Settings2 className="size-4" />
                  )}
                  Abo verwalten / kündigen
                </button>
              </div>
            </div>
          ) : plans.length === 0 ? (
            <div className="py-10 text-center text-[13px] text-slate-500">
              Derzeit keine Abo-Pläne verfügbar.
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-3">
              {plans.map((plan, i) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  content={getPlanContent(plan)}
                  cycle={cycle}
                  discountPct={discountForPlan(plan.credits_per_month, plan.price_monthly_cents)}
                  highlight={plan.id === RECOMMENDED_PLAN_ID}
                  inFlight={subPlanInFlight === plan.id}
                  disabled={subInFlight}
                  index={i}
                  onSubscribe={(id) => void handleSubscribe(id)}
                />
              ))}
            </div>
          )}

          {subError ? (
            <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-2.5 text-[13px] text-rose-300">
              {subError}
            </div>
          ) : null}
        </section>

        {/* ── Einzel-Pack-Sektion (sekundär) ───────────────────────── */}
        <section className="space-y-5 border-t border-white/5 pt-8">
          <div>
            <h2 className="text-[15px] font-bold text-slate-200">
              Einmalig nachkaufen — läuft nie ab
            </h2>
            <p className="mt-0.5 text-[12px] text-slate-500">
              Einmalzahlung, Credits verfallen nicht.
            </p>
          </div>

          {loading ? (
            <div className="py-8 text-center text-[13px] text-slate-500">
              <Loader2 className="mx-auto mb-2 size-5 animate-spin" />
              Lade Pakete…
            </div>
          ) : error ? (
            <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-2.5 text-[13px] text-rose-300">
              {error}
            </div>
          ) : packs.length === 0 ? (
            <div className="py-8 text-center text-[13px] text-slate-500">
              Derzeit keine Pakete verfügbar.
            </div>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-3">
                {packs.map((pack, i) => (
                  <PackCard
                    key={pack.id}
                    pack={pack}
                    inFlight={checkoutPackId === pack.id}
                    disabled={checkoutPackId !== null}
                    index={i}
                    onBuy={(id) => void handleBuy(id)}
                  />
                ))}
              </div>
              {checkoutError ? (
                <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-2.5 text-[13px] text-rose-300">
                  {checkoutError}
                </div>
              ) : null}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
